import { intlFormat, isValid, parseISO } from "date-fns"

import { GUILD_EVENTS_SCHEDULE_CONFIG } from "@/core/guild-events/types"

const guildLocale = "en-GB"

const coerceDate = (value: string | Date): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  const parsedIso = parseISO(value)
  if (isValid(parsedIso)) {
    return parsedIso
  }

  const fallback = new Date(value)
  return Number.isNaN(fallback.getTime()) ? null : fallback
}

export const formatGuildDate = (value: string | Date): string => {
  const date = coerceDate(value)
  if (!date) {
    return ""
  }

  return intlFormat(
    date,
    {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    },
    { locale: guildLocale },
  )
}

export const formatGuildIsoDate = (value: string | Date): string => {
  const date = coerceDate(value)
  if (!date) {
    return ""
  }

  return intlFormat(
    date,
    {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    },
    { locale: "en-CA" },
  )
}

export const formatGuildDateTime = (value: string | Date): string => {
  const date = coerceDate(value)
  if (!date) {
    return ""
  }

  return intlFormat(
    date,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    },
    { locale: guildLocale },
  )
}

export const formatGuildEventDateTime = (value: string | Date): string => {
  const date = coerceDate(value)
  if (!date) {
    return ""
  }

  return intlFormat(
    date,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: GUILD_EVENTS_SCHEDULE_CONFIG.timezone,
    },
    { locale: guildLocale },
  )
}

export const formatGuildEventDateTimeWithTimezone = (
  value: string | Date,
): string => {
  const date = coerceDate(value)
  if (!date) {
    return ""
  }

  return intlFormat(
    date,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: GUILD_EVENTS_SCHEDULE_CONFIG.timezone,
      timeZoneName: "short",
    },
    { locale: guildLocale },
  )
}

export const getTodayGuildIsoDate = (): string => {
  return formatGuildIsoDate(new Date())
}

export const formatGuildDateRange = (
  startAt: string,
  endAt: string,
): string => {
  const start = formatGuildDate(startAt)
  const end = formatGuildDate(endAt)
  if (!start || !end) {
    return start || end
  }

  return start === end ? start : `${start} - ${end}`
}

export const formatDateTimeOffsetHint = (value: string): string => {
  const date = coerceDate(value)
  if (!date) {
    return ""
  }

  const local = date.toLocaleString(undefined, { timeZoneName: "shortOffset" })
  return `${local} · ${date.toISOString()}`
}
